import { useState } from 'react'
import { Bell, X, CheckCheck, MessageSquare, AlertTriangle, UserCheck, Calendar, Clock, Shield, Megaphone, Info } from 'lucide-react'
import { cn } from '@/lib/utils'

const typeIcons = {
  mention: MessageSquare,
  comment: MessageSquare,
  reply: MessageSquare,
  assignment: UserCheck,
  expiry_warning: AlertTriangle,
  status_change: Info,
  invitation: UserCheck,
  check_in: Clock,
  approval: CheckCheck,
  team_update: UserCheck,
  system: Shield,
  leave: Calendar,
  alert: Megaphone,
}

const typeColors = {
  mention: 'text-blue-500 bg-blue-50',
  comment: 'text-blue-500 bg-blue-50',
  reply: 'text-blue-500 bg-blue-50',
  assignment: 'text-purple-500 bg-purple-50',
  expiry_warning: 'text-amber-500 bg-amber-50',
  status_change: 'text-emerald-500 bg-emerald-50',
  invitation: 'text-indigo-500 bg-indigo-50',
  check_in: 'text-cyan-500 bg-cyan-50',
  approval: 'text-emerald-500 bg-emerald-50',
  team_update: 'text-violet-500 bg-violet-50',
  system: 'text-gray-500 bg-gray-50',
  leave: 'text-emerald-500 bg-emerald-50',
  alert: 'text-red-500 bg-red-50',
}

function timeAgo(dateStr) {
  const date = new Date(dateStr)
  const now = new Date()
  const seconds = Math.floor((now - date) / 1000)

  if (seconds < 60) return 'Just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

export default function NotificationDrawer({ open, onClose, notifications, unreadCount, onMarkAsRead, onMarkAllAsRead, loading }) {
  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-x-0 top-0 bottom-0 z-50 flex justify-end">
        <div className="w-full max-w-sm bg-white shadow-xl flex flex-col animate-in slide-in-from-right duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">Notifications</h2>
              {unreadCount > 0 && (
                <span className="bg-emerald-100 text-emerald-700 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={onMarkAllAsRead}
                  className="text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-50 transition-colors"
                >
                  Mark all read
                </button>
              )}
              <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Notification list */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <div className="h-5 w-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <Bell className="h-10 w-10 text-gray-200 mb-3" />
                <p className="text-sm text-gray-500">No notifications yet</p>
                <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map(n => {
                  const Icon = typeIcons[n.type] || Bell
                  const colorClass = typeColors[n.type] || 'text-gray-500 bg-gray-50'

                  return (
                    <button
                      key={n.id}
                      onClick={() => {
                        if (!n.is_read) onMarkAsRead(n.id)
                      }}
                      className={cn(
                        'w-full text-left px-4 py-3 flex gap-3 transition-colors',
                        !n.is_read ? 'bg-emerald-50/40' : 'hover:bg-gray-50'
                      )}
                    >
                      {/* Icon */}
                      <div className={cn('shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5', colorClass)}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={cn('text-sm leading-snug', !n.is_read ? 'font-medium text-gray-900' : 'text-gray-700')}>
                            {n.title}
                          </p>
                          {!n.is_read && (
                            <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 mt-1.5" />
                          )}
                        </div>
                        {n.message && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1">{timeAgo(n.created_date)}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
